import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/button";
import { Copy, Loader2, Wifi } from "lucide-react";

type Props = {
  callbackUrl: string | null;
  homepageUrl: string | null;
  callbackDomain: string | null;
  onCopy: (text: string, label: string) => void;
  onTest: () => void;
  isTesting: boolean;
  /** B2 #8⑥:非管理员(经理)只读 —— 「测试连接」会真打企微,不给非管理员点。缺省 true 兼容老调用。 */
  canTest?: boolean;
};

/** 集成端点只读展示 + 复制 + 测试连接。homepageUrl 仅军师启用(TENANT_CHAIN)时由后端派生,故 null 即不渲染。 */
export function IntegrationEndpoints({
  callbackUrl,
  homepageUrl,
  callbackDomain,
  onCopy,
  onTest,
  isTesting,
  canTest = true,
}: Props) {
  const { t } = useTranslation("wecom");
  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2.5 space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="text-2xs font-medium uppercase tracking-wide text-muted-foreground/70">
          {t("appList.sectionEndpoints")}
          <span className="ml-2 normal-case font-normal">{t("appList.sectionEndpointsHint")}</span>
        </div>
        {canTest && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-2xs"
            onClick={onTest}
            disabled={isTesting}
          >
            {isTesting ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Wifi className="h-3 w-3 mr-1" />
            )}
            {t("appList.testConnection")}
          </Button>
        )}
      </div>
      {callbackUrl && (
        <Row label={t("appCard.callbackUrlLabel")} value={callbackUrl} onCopy={onCopy} />
      )}
      {homepageUrl && (
        <Row label={t("appCard.homepageUrlLabel")} value={homepageUrl} onCopy={onCopy} />
      )}
      {callbackDomain && (
        <Row label={t("appList.endpointAuthDomain")} value={callbackDomain} onCopy={onCopy} />
      )}
    </div>
  );
}

function Row({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (text: string, label: string) => void;
}) {
  const { t } = useTranslation("wecom");
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground min-w-[9rem] shrink-0 whitespace-nowrap">{label}</span>
      <span className="flex-1 min-w-0 font-mono text-2xs truncate" title={value}>
        {value}
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 shrink-0"
        onClick={() => onCopy(value, label)}
        title={t("appCard.copyTooltip", { label })}
        aria-label={t("appCard.copyTooltip", { label })}
      >
        <Copy className="h-3 w-3" />
      </Button>
    </div>
  );
}
